<template>
  <div id="summary-graph">
    <div class="sac__font-18 semi-bold">
      Performance Summary
    </div>
    <div class="grid-column jc-s">
      <SACDropdown
        field="day"
        :options="filterDays"
        :value="startDate"
        :change="changeDate"
      />
    </div>
    <div>
      <div
        v-if="loadingSummary"
        class="loading-container"
      >
        <img src="@shared/assets/img/general/loading.svg" alt="loading">
      </div>
      <div v-else class="grid-row gap-16">
        <highcharts
          ref="highchartsDeltaT"
          :options="deltaTGraphOptions"
        />
        <highcharts
          ref="highchartsRuntime"
          :options="runtimeGraphOptions"
        />
        <highcharts
          ref="highchartsOaTemp"
          :options="oaTempGraphOptions"
        />
        <highcharts
          ref="highchartsFilterLife"
          :options="filterLifeGraphOptions"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import SACDropdown from '@shared/components/sac/SACDropdown.vue';
import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';

export default {
  name: 'SummaryGraph',
  components: { SACDropdown },
  mixins: [customerDetailMixin, unitPreferencesMixin],
  data() {
    return {
      selectedUID: '',
      filterDays: [
        { label: 'Last 30 days', value: moment().subtract(30, 'days').format('YYYY-MM-DD') },
        { label: 'Last 60 days', value: moment().subtract(60, 'days').format('YYYY-MM-DD') },
        { label: 'Last 90 days', value: moment().subtract(90, 'days').format('YYYY-MM-DD') },
      ],
      startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    };
  },
  computed: {
    ...mapState({
      loadingSummary: (state) => state.customerDetail.loadingSummary,
      selectedUnit: (state) => state.customerDetail.selectedUnit,
    }),
    ...mapGetters([
      'summaryGraph',
    ]),
    xAxis() {
      return this.summaryGraph.dateList;
    },
    filterLifeGraphOptions() {
      const {
        filterLifeList,
      } = this.summaryGraph;
      return {
        chart: {
          zoomType: 'x',
          height: 200,
          backgroundColor: '#F3F4F6',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          positioner() {
            return {
              x: this.chart.chartWidth - this.label.width,
              y: 10, // align to title
            };
          },
          borderWidth: 0,
          backgroundColor: 'none',
          headerFormat: '',
          shadow: false,
          style: {
            fontSize: 12,
            fontWeight: 'bold',
          },
          formatter() {
            return `${this.series.name}: ${this.y}%`;
          },
        },
        xAxis: [{
          crosshair: {
            width: 1,
            color: 'gray',
            dashStyle: 'shortdot',
          },
          categories: this.xAxis,
          title: {
            text: '',
          },
          labels: {
            formatter() {
              return moment(this.value).format('MM/DD');
            },
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
        }],
        yAxis: [{
          labels: {
            format: '{value}',
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
          title: {
            text: 'Percentage',
            style: {
              fontWeight: 'bold',
              color: 'black',
            },
          },
          gridLineWidth: 0.2,
          tickInterval: 25,
          min: 0,
          max: 100,
        }],
        legend: {
          shadow: false,
          align: 'left',
          verticalAlign: 'top',
          x: 80,
          y: 0,
          itemStyle: {
            color: 'black',
          },
        },
        plotOptions: {
          series: {
            point: {
              events: {
                mouseOver: this.sync,
                mouseOut: this.sync,
              },
            },
          },
        },
        series: [{
          type: 'line',
          yAxis: 0,
          xAxis: 0,
          name: 'Filter Life',
          color: '#55EA9D',
          data: filterLifeList,
          marker: {
            radius: 0,
          },
        }],
      };
    },
    deltaTGraphOptions() {
      const {
        coolDeltaTList,
        heatDeltaTList,
      } = this.summaryGraph;
      const { TEMP_UNIT: tempFormat = '°F' } = this.customerPartnerSettings;

      const coolList = coolDeltaTList?.map((c) => (tempFormat === '°C' ? convertToCelsiusResult(c) : c));
      const heatList = heatDeltaTList?.map((c) => (tempFormat === '°C' ? convertToCelsiusResult(c) : c));
      return {
        chart: {
          zoomType: 'x',
          height: 200,
          backgroundColor: '#F3F4F6',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          positioner() {
            return {
              x: this.chart.chartWidth - this.label.width,
              y: 10, // align to title
            };
          },
          borderWidth: 0,
          backgroundColor: 'none',
          headerFormat: '',
          shadow: false,
          style: {
            fontSize: 12,
            fontWeight: 'bold',
          },
          formatter() {
            if (this.y) {
              return `Delta T: ${this.y}${tempFormat}`;
            }
            return '';
          },
        },
        xAxis: [{
          crosshair: {
            width: 1,
            color: 'gray',
            dashStyle: 'shortdot',
          },
          categories: this.xAxis,
          title: {
            text: '',
          },
          labels: {
            formatter() {
              return moment(this.value).format('MM/DD');
            },
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
        }],
        yAxis: [{
          labels: {
            format: `{value}${tempFormat}`,
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
          title: {
            text: 'Temp',
            style: {
              fontWeight: 'bold',
              color: 'black',
            },
          },
          gridLineWidth: 0.2,
          tickInterval: 5,
          min: Math.min(...coolList, ...heatList) - 4,
          max: Math.max(...coolList, ...heatList) + 4,
        }],
        legend: {
          shadow: false,
          align: 'left',
          verticalAlign: 'top',
          x: 80,
          y: 0,
          itemStyle: {
            color: 'black',
          },
        },
        plotOptions: {
          series: {
            point: {
              events: {
                mouseOver: this.sync,
                mouseOut: this.sync,
              },
            },
          },
        },
        series: [{
          type: 'line',
          yAxis: 0,
          xAxis: 0,
          name: 'Average Daily Cooling Performance',
          color: '#544FC5',
          data: coolList.map((el) => (!el ? null : el)),
          marker: {
            radius: 4,
            fillColor: '#544FC5',
          },
        }, {
          type: 'line',
          yAxis: 0,
          xAxis: 0,
          name: 'Average Daily Heating Performance',
          color: '#EF5B52',
          data: heatList.map((el) => (!el ? null : el)),
          marker: {
            radius: 4,
            fillColor: '#EF5B52',
          },
        }],
      };
    },
    runtimeGraphOptions() {
      const {
        runtimeList,
      } = this.summaryGraph;

      return {
        chart: {
          zoomType: 'x',
          height: 200,
          backgroundColor: '#F3F4F6',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          positioner() {
            return {
              x: this.chart.chartWidth - this.label.width,
              y: 10, // align to title
            };
          },
          borderWidth: 0,
          backgroundColor: 'none',
          headerFormat: '',
          shadow: false,
          style: {
            fontSize: 12,
            fontWeight: 'bold',
          },
          formatter() {
            return `${this.y} hrs`;
          },
        },
        xAxis: [{
          crosshair: {
            width: 1,
            color: 'gray',
            dashStyle: 'shortdot',
          },
          categories: this.xAxis,
          title: {
            text: '',
          },
          labels: {
            formatter() {
              return moment(this.value).format('MM/DD');
            },
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
        }],
        yAxis: [{
          min: 0,
          max: Math.max(...runtimeList) + 4,
          gridLineWidth: 0.2,
          tickInterval: 6,
          title: {
            text: 'Hours',
            style: {
              fontWeight: 'bold',
              color: 'black',
            },
          },
          stackLabels: {
            enabled: true,
            style: {
              fontWeight: 'bold',
              color: 'white',
            },
          },
          labels: {
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
        }],
        legend: {
          shadow: false,
          align: 'left',
          verticalAlign: 'top',
          x: 80,
          y: 0,
          itemStyle: {
            color: 'black',
          },
        },
        plotOptions: {
          series: {
            point: {
              events: {
                mouseOver: this.sync,
                mouseOut: this.sync,
              },
            },
          },
        },
        series: [{
          type: 'area',
          yAxis: 0,
          xAxis: 0,
          name: 'Total Daily Runtime',
          color: '#808080',
          data: runtimeList,
          marker: {
            radius: 0,
          },
        }],
      };
    },
    oaTempGraphOptions() {
      const {
        maxTempList,
        minTempList,
      } = this.summaryGraph;
      const { TEMP_UNIT: tempFormat = '°F' } = this.customerPartnerSettings;

      const maxList = maxTempList.map((t) => (tempFormat === '°C' ? convertToCelsiusResult(t) : t));
      const minList = minTempList.map((t) => (tempFormat === '°C' ? convertToCelsiusResult(t) : t));
      return {
        chart: {
          zoomType: 'x',
          height: 200,
          backgroundColor: '#F3F4F6',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        tooltip: {
          positioner() {
            return {
              x: this.chart.chartWidth - this.label.width,
              y: 10, // align to title
            };
          },
          borderWidth: 0,
          backgroundColor: 'none',
          headerFormat: '',
          shadow: false,
          style: {
            fontSize: 12,
            fontWeight: 'bold',
          },
          formatter() {
            return `Outside Temp: ${this.y}${tempFormat}`;
          },
        },
        xAxis: [{
          crosshair: {
            width: 1,
            color: 'gray',
            dashStyle: 'shortdot',
          },
          categories: this.xAxis,
          title: {
            text: '',
          },
          labels: {
            formatter() {
              return moment(this.value).format('MM/DD');
            },
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
        }],
        yAxis: [{
          labels: {
            format: `{value}${tempFormat}`,
            style: {
              textOutline: 0,
              fontWeight: 'bold',
              fontSize: 8,
              color: 'black',
            },
          },
          title: {
            text: 'Temp',
            style: {
              fontWeight: 'bold',
              color: 'black',
            },
          },
          gridLineWidth: 0.2,
          tickInterval: 20,
          min: Math.min(...minList) - 4,
          max: Math.max(...maxList) + 4,
        }],
        legend: {
          shadow: false,
          align: 'left',
          verticalAlign: 'top',
          x: 80,
          y: 0,
          itemStyle: {
            color: 'black',
          },
        },
        plotOptions: {
          series: {
            point: {
              events: {
                mouseOver: this.sync,
                mouseOut: this.sync,
              },
            },
          },
        },
        series: [{
          type: 'area',
          yAxis: 0,
          xAxis: 0,
          name: 'Min Daily Outside Air Temperature',
          color: '#FFEABF',
          borderColor: '#FFAB00',
          data: minList,
          zIndex: 2,
          marker: {
            radius: 0,
          },
        }, {
          type: 'area',
          yAxis: 0,
          xAxis: 0,
          name: 'Max Daily Outside Air Temperature',
          color: '#FF8C86',
          borderColor: '#F7433A',
          data: maxList,
          zIndex: 1,
          marker: {
            radius: 0,
          },
        }],
      };
    },
  },
  methods: {
    changeDate(_, value) {
      this.startDate = value;
      this.initialLoad(this.selectedUnit.uid, value);
    },
    sync(event) {
      const type = event.type === 'mouseOver' ? 'over' : 'out';
      const refs = [this.$refs.highchartsDeltaT, this.$refs.highchartsRuntime, this.$refs.highchartsOaTemp, this.$refs.highchartsFilterLife];
      refs.forEach(({ chart }) => {
        chart.series.forEach((series) => {
          series.data.forEach((point) => {
            if (point.x === event.target.x) {
              if (type === 'over') {
                point.setState('hover');
                chart.tooltip.refresh(point);
                chart.xAxis[0].drawCrosshair(event, point);
              } else {
                point.setState();
                chart.tooltip.hide();
                chart.xAxis[0].hideCrosshair();
              }
            }
          });
        });
      });
    },
    initialLoad(UID, startDate) {
      this.$store.dispatch('getDailyStats', {
        UID,
        startDate,
      });
    },
  },
};
</script>

<style scoped lang="scss">
#summary-graph {
  background-color: $WHITE;
  border-radius: 8px;
  display: grid;
  gap: 24px;
}
</style>
